import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import {
  AdditionalCost,
  FeeInformation,
  FinancialInformationData,
  ProductInformation,
  ServiceDetail,
} from 'src/app/models/service-detail/service-detail.model';

import { AddCostModalComponent } from './components/add-cost-modal/add-cost-modal.component';
import { SaveChangesModalComponent } from './components/save-changes-modal/save-changes-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { getProductIcon } from 'src/app/utils/icons-by-product.helper';
import { FeeTypeEnum, FeeTypeLabel } from 'src/app/enum/fees/fees.enum';
import { filter, finalize, Subscription } from 'rxjs';
import {
  additionalProduct,
  grayTrashIcon,
  lucideIcon,
  plusBlackIcon,
} from 'src/app/utils/icons-path';
import {
  SaveChangesResult,
  UIPriceItem,
  UIPriceItemGroup,
  UpdateFinancialRequest,
} from 'src/app/models/financial-information/financial-information.model';
import { FinancialInformationService } from 'src/app/services/financial-information/financial-information.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TuiAlertService } from '@taiga-ui/core';
import {
  CancelEditResult,
  SaveWithoutChangeModalComponent,
} from './components/save-without-change-modal/save-without-change-modal.component';

const NON_EDITABLE_PRODUCT_IDS = [
  '1c174844-abdb-403c-b0f1-2443d30485f6', // Repostaje
];

@Component({
  selector: 'financial-information',
  templateUrl: './financial-information.component.html',
  styleUrls: ['./financial-information.component.css'],
})
export class FinancialInformationComponent
  implements OnChanges, OnDestroy
{
  @Input() serviceDetail!: ServiceDetail;
  @Input() currency: string = '€';

  @Output() serviceDetailChanged = new EventEmitter<ServiceDetail>();

  isEditMode = false;
  totalTaxFree = 0;
  totalTax = 0;
  totalWithTax = 0;

  readonly MAX_INPUT_LENGTH = 6;
  readonly MAX_AMOUNT = 9_999_999;

  form!: FormGroup;
  private _formSub?: Subscription;
  public FeeTypeEnum = FeeTypeEnum;

  grayInfoIcon = lucideIcon;
  plusBlackIcon = plusBlackIcon;
  additionalProduct = additionalProduct;
  trashIcon = grayTrashIcon;

  saving: boolean = false;
  private pendingComment: string = '';
  private modalRef?: BsModalRef;

  private readonly NBSP_NARROW = '\u202F';

  get currencyParam(): string {
    const symbol = (this.currency ?? '').toString().trim() || '€';
    return this.NBSP_NARROW + symbol;
  }

  private get localeForFormatting(): string {
    const lang = this.translate.currentLang ?? 'es';
    const map: Record<string, string> = {
      es: 'es-ES',
      en: 'en-GB',
      fr: 'fr-FR',
    };
    return map[lang] ?? lang;
  }

  get formattedMinAmount(): string {
    return new Intl.NumberFormat(this.localeForFormatting, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);
  }

  get formattedMaxAmount(): string {
    return new Intl.NumberFormat(this.localeForFormatting, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.MAX_AMOUNT);
  }

  get errorAmountMinText(): string {
    return this.translate.instant('financial-information.error_amount_min', {
      min: this.formattedMinAmount,
      currency: this.currencyParam,
    });
  }

  get errorAmountMaxText(): string {
    return this.translate.instant('financial-information.error_amount_max', {
      max: this.formattedMaxAmount,
      currency: this.currencyParam,
    });
  }
  // ------------------------------------------------------------------------------------

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private financialInformationService: FinancialInformationService,
    private modalService: BsModalService,
    private alerts: TuiAlertService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceDetail'] && this.serviceDetail) {
      this.rebuildForm(this.mapFinancial(this.serviceDetail));
      this.form.disable({ emitEvent: false });
    }
  }

  get productsFA(): FormArray {
    return this.form.get('products') as FormArray;
  }
  get addCostsFA(): FormArray {
    return this.form.get('addCosts') as FormArray;
  }
  get feesFA(): FormArray {
    return this.form.get('fees') as FormArray;
  }
  get hasPendingChanges(): boolean {
    return this.isEditMode && this.form.dirty;
  }

  enterEditMode(): void {
    if (this.isEditMode) return;
    this.isEditMode = true;
    this.form.enable({ emitEvent: false });
    this.lockRefuelProducts();
  }

  limitLengthOnKeydown(
    event: KeyboardEvent,
    control: AbstractControl | null
  ): void {
    const input = event.target as HTMLInputElement;
    const key = event.key;

    const allowedKeys = [
      'Backspace',
      'Tab',
      'End',
      'Home',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
    ];
    if (allowedKeys.includes(key)) return;

    if (key === '.') {
      if (input.value.includes('.')) event.preventDefault();
      return;
    }

    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return;
    }

    const digitsCount = input.value.replace(/\D/g, '').length;
    if (digitsCount >= this.MAX_INPUT_LENGTH) {
      event.preventDefault();
    }
  }

  cancelEdit(): void {
    if (!this.isEditMode) return;

    if (!this.form.dirty) {
      this.exitEditModeAndReset();
      return;
    }

    const bsRef = this.modalService.show(SaveWithoutChangeModalComponent, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: false,
    });

    const comp = bsRef.content as SaveWithoutChangeModalComponent;

    comp.onClose.subscribe((res: CancelEditResult) => {
      if (res.discard) {
        this.exitEditModeAndReset();
      }
      bsRef.hide();
    });
  }

  private exitEditModeAndReset(): void {
    this.isEditMode = false;
    this.rebuildForm(this.mapFinancial(this.serviceDetail));
    this.form.disable({ emitEvent: false });
  }

  addAdditionalCost(): void {
    this.isEditMode = false;
    const bsRef: BsModalRef = this.modalService.show(AddCostModalComponent, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      initialState: {
        orderHash: this.serviceDetail.orderHash,
        currency: this.currency,
      },
    });

    const comp = bsRef.content as AddCostModalComponent;

    comp.onClose.subscribe((res) => {
      if (!res.confirmed) return;

      this.serviceDetailChanged.emit(this.serviceDetail);
    });
  }

  getProductIcon(productKey: string): string | undefined {
    return getProductIcon(productKey);
  }

  saveChanges(): void {
    if (this.saving) {
      return;
    }

    if (!this.hasPendingChanges) {
      this.alerts
        .open(
          this.translate.instant('financial-information.error_no_changes'),
          { status: 'warning', autoClose: 3000 }
        )
        .subscribe();
      return;
    }

    this.openSaveModal();
  }

  private openSaveModal(): void {
    this.modalRef = this.modalService.show(SaveChangesModalComponent, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: false,
    });
    const comp = this.modalRef.content as SaveChangesModalComponent;

    comp.onClose
      .pipe(filter((res: SaveChangesResult) => res.confirmed))
      .subscribe((res) => this.handleModalConfirm(res));
  }

  private handleModalConfirm(res: SaveChangesResult): void {
    this.applyFormToServiceDetail();
    this.pendingComment = res.comment ?? '';
    this.sendingUpdate();
  }

  removeAdditionalCost(index: number): void {
    this.addCostsFA.removeAt(index);
    this.computeTotals();
    this.form.markAsDirty();
  }

  private sendingUpdate(): void {
    const payload: UpdateFinancialRequest = {
      serviceGuid: this.serviceDetail.serviceGuid,
      orderHash: this.serviceDetail.orderHash,
      message: this.pendingComment,
      productInformation:
        this.serviceDetail.financialInformationData.productInformation,
      feeInformation:
        this.serviceDetail.financialInformationData.feeInformation,
      additionalCostInformation:
        this.serviceDetail.financialInformationData.additionalCostInformation,
    };

    this.saving = true;
    this.financialInformationService
      .updateFinancial(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (resp) => this.onUpdateSuccess(resp.isSuccess),
        error: (err) => this.onUpdateError(err),
      });
  }

  private onUpdateSuccess(success: boolean): void {
    if (success) {
      this.isEditMode = false;
      this.form.markAsPristine();
      this.form.disable({ emitEvent: false });
      this.serviceDetailChanged.emit(this.serviceDetail);
      this.alerts
        .open(this.translate.instant('financial-information.success_saved'), {
          status: 'success',
          autoClose: 3000,
        })
        .subscribe();
    } else {
      this.alerts
        .open(
          this.translate.instant('financial-information.error_could_not_save'),
          {
            status: 'error',
            autoClose: 3000,
          }
        )
        .subscribe();
    }
  }

  private onUpdateError(err: any): void {
    console.error(err);
    this.alerts
      .open(this.translate.instant('financial-information.error_update'), {
        status: 'error',
        autoClose: 3000,
      })
      .subscribe();
  }

  isFeeUnderProduct(ctrl: AbstractControl): boolean {
    const { feeType, parentProductIndex } = ctrl.value as {
      feeType?: FeeTypeEnum;
      parentProductIndex?: number | null;
    };

    return (
      feeType === FeeTypeEnum.INTERNAL_REFUELING_TOPPING_ADDITIONAL_FEE &&
      parentProductIndex !== null &&
      parentProductIndex !== undefined
    );
  }

  private computeTotals(): void {
    const r2 = (n: number) => Math.round(n * 100) / 100;

    if (!this.form.dirty) {
      const fi = this.serviceDetail.financialInformationData;

      this.totalTaxFree = r2(fi.totalPriceWithoutTax);
      this.totalTax = r2(fi.taxPrice);
      this.totalWithTax = r2(fi.totalPrice);
      return;
    }

    const productsRaw = this.productsFA.getRawValue() as UIPriceItem[];

    const productsGrouped: number[] = Object.values(
      productsRaw.reduce<Record<string, number>>((acc, p) => {
        const amount = +p.amount || 0;
        acc[p.key] = r2((acc[p.key] ?? 0) + amount);
        return acc;
      }, {})
    );

    const addCostsTotalArr = (
      this.addCostsFA.getRawValue() as UIPriceItem[]
    ).map((c) => r2(+c.amount || 0));

    const feesTotalArr = (this.feesFA.getRawValue() as UIPriceItem[]).map((f) =>
      r2(+f.amount || 0)
    );

    this.totalTaxFree = r2(
      [...productsGrouped, ...addCostsTotalArr, ...feesTotalArr].reduce(
        (t, n) => t + n,
        0
      )
    );

    const pct = +this.form.get('taxPercentage')!.value || 0;
    this.totalTax = r2((this.totalTaxFree * pct) / 100);
    this.totalWithTax = r2(this.totalTaxFree + this.totalTax);
  }

  private rebuildForm(
    data: FinancialInformationData & {
      ui: {
        products: UIPriceItem[];
        costs: UIPriceItem[];
        fees: UIPriceItem[];
      };
    }
  ): void {
    this._formSub?.unsubscribe();

    this.form = this.fb.nonNullable.group({
      products: this.fb.nonNullable.array(data.ui.products.map(ctrlFromUIItem)),
      addCosts: this.fb.nonNullable.array(data.ui.costs.map(ctrlFromUIItem)),
      fees: this.fb.nonNullable.array(data.ui.fees.map(ctrlFromUIItem)),
      taxPercentage: new FormControl<number>(data.tax * 100, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
    });

    this._formSub = this.form.valueChanges.subscribe(() => {
      if (this.isEditMode) this.computeTotals();
    });

    this.computeTotals();
    this.lockRefuelProducts();

    if (!this.isEditMode) {
      this.form.disable({ emitEvent: false });
    }
  }

  private lockRefuelProducts(): void {
    this.productsFA.controls.forEach((ctrl) => {
      const productId = ctrl.get('key')!.value as string;

      if (NON_EDITABLE_PRODUCT_IDS.includes(productId)) {
        ctrl.get('amount')?.disable({ emitEvent: false });
      }
    });
  }

  getProductNameTranslatedByProductName(productName: string): string {
    if (!productName) return '–';

    const key = 'common.services.' + productName;
    const translated = this.translate.instant(key);

    return translated === key ? productName : translated;
  }

  isRefueling(label: string): boolean {
    const norm = label.toLowerCase();
    return norm.includes('refueling') || norm.includes('repostaje');
  }

  getRefuelFee(productIdx: number): FormGroup | null {
    return this.feesFA.controls.find((ctrl) => {
      const v = ctrl.value as UIPriceItem;
      return (
        v.parentProductIndex === productIdx &&
        v.feeType === FeeTypeEnum.INTERNAL_REFUELING_TOPPING_ADDITIONAL_FEE
      );
    }) as FormGroup | null;
  }

  getProductLabel(name: string): string {
    return this.getProductNameTranslatedByProductName(name);
  }

  private mapFinancial(detail: ServiceDetail): FinancialInformationData & {
    ui: {
      products: UIPriceItem[];
      costs: UIPriceItem[];
      fees: UIPriceItem[];
    };
  } {
    const fi = detail.financialInformationData;

    const prodInfo = fi.productInformation ?? [];
    const costInfo = fi.additionalCostInformation ?? [];
    const feeInfo = fi.feeInformation ?? [];

    const productIndexByName = new Map<string, number>();

    const uiProducts: UIPriceItem[] = prodInfo.map((p, idx) => {
      productIndexByName.set(this.normalize(p.productName), idx);

      return {
        key: p.productGuid,
        label: p.productName,
        amount: p.amount,
        taxable: true,
        icon: getProductIcon(p.productName) ?? undefined,
      };
    });

    const uiCosts: UIPriceItem[] = costInfo.map((c) => ({
      key: crypto.randomUUID(),
      label: c.additionalCostName,
      amount: c.amount,
      taxable: true,
    }));

    const idToFeeType: Record<number, FeeTypeEnum> = {
      1: FeeTypeEnum.CAFLER_FEE,
      2: FeeTypeEnum.FIXED_TIME,
      3: FeeTypeEnum.URGENCY,
      100: FeeTypeEnum.INTERNAL_REFUELING_TOPPING_ADDITIONAL_FEE,
    };

    const uiFees: UIPriceItem[] = feeInfo.map((f) => {
      const feeType = idToFeeType[f.feeId] ?? FeeTypeEnum.UNKNOWN;

      let parentProductIndex: number | undefined;

      if (feeType === FeeTypeEnum.INTERNAL_REFUELING_TOPPING_ADDITIONAL_FEE) {
        for (const [normName, idx] of productIndexByName) {
          if (
            normName.includes('refueling') ||
            normName.includes('repostaje')
          ) {
            parentProductIndex = idx;
            break;
          }
        }
      }

      return {
        key: `${f.feeId}`,
        label: this.translate.instant(FeeTypeLabel[feeType]),
        amount: f.feeAmount,
        taxable: true,
        feeType,
        parentProductIndex,
      } as UIPriceItem;
    });

    return {
      ...fi,
      ui: {
        products: uiProducts,
        costs: uiCosts,
        fees: uiFees,
      },
    };
  }

  private normalize(str: string): string {
    return str.toLowerCase().replace(/\s+/g, '');
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    this.modalRef?.hide();
    this.modalRef = undefined;
  }

  private applyFormToServiceDetail(): void {
    const f = this.form.getRawValue();

    const valueArr = (arr: unknown) => arr as UIPriceItem[];

    const toProductInfo = (u: UIPriceItem): ProductInformation => ({
      productGuid: u.key,
      productName: u.label,
      amount: u.amount,
    });

    const toAddCost = (u: UIPriceItem): AdditionalCost => ({
      additionalCostName: u.label,
      amount: u.amount,
    });

    const toFee = (u: UIPriceItem): FeeInformation => ({
      feeId: Number(u.key),
      feeAmount: u.amount,
    });

    const fin: FinancialInformationData = {
      productInformation: valueArr(f.products).map(toProductInfo),
      additionalCostInformation: valueArr(f.addCosts).map(toAddCost),
      feeInformation: valueArr(f.fees).map(toFee),

      tax: f.taxPercentage / 100,
      taxPrice: this.totalTax,
      totalPrice: this.totalWithTax,
      totalPriceWithoutTax: this.totalTaxFree,
      currencySymbol: this.currency,

      freelanceCustomPrice:
        this.serviceDetail.financialInformationData.freelanceCustomPrice,
    };

    this.serviceDetail.financialInformationData = fin;
  }
}

function ctrlFromUIItem(item: UIPriceItem): UIPriceItemGroup {
  return new FormGroup({
    key: new FormControl(item.key, { nonNullable: true }),
    label: new FormControl(item.label, { nonNullable: true }),
    amount: new FormControl(item.amount, {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(9999999),
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
      ],
    }),
    taxable: new FormControl(item.taxable, { nonNullable: true }),

    feeType: new FormControl(item.feeType ?? null),
    parentProductIndex: new FormControl(item.parentProductIndex ?? null),
    icon: new FormControl(item.icon ?? null),
  });
}
